/**
 * A "prompt" is a set of instructions that is fed to the bot to prime it
 * to respond to inquiries in a way that we want. We use prompt templates
 * that contain variables which are interpolated into the final prompt we
 * provide the bot. This map contains variables that we've set and are
 * available for prompt engineering.
 *
 * TODO(tony): Type this with prompt variable names that we support/expect.
 */
let globalVariables: Record<string, string> = {};

/**
 * Set a prompt variable. If the value is nullish, the variable will be
 * deleted
 */
export const setPromptVariable = (key: string, value?: string | null): void => {
    if (value == null) {
        delete globalVariables[key];
    } else {
        globalVariables[key] = value;
    }
};

/**
 * Set multiple prompt variables.
 *
 * The object passed to this method is combined with the existing variables,
 * overriding any existing variables with the same name. This does not clear
 * any variables.
 */
export const setPromptVariables = (variables: Record<string, string>): void => {
    globalVariables = {...globalVariables, ...variables};
};

/**
 * Get all the prompt variables.
 */
export const getPromptVariables = (): Record<string, string> => ({
    ...globalVariables,
});

/**
 * Get a prompt variable.
 */
export const getPromptVariable = (key: string): string | null | undefined =>
    globalVariables[key];

/**
 * Clear all prompt variables.
 *
 * An optional `options` object can be passed into the function to allow for
 * more control. The `options.exclude` config can be used to exempt specified
 * prompt variables from being cleared.
 */
export const clearPromptVariables = (options?: {
    exclude?: Array<string>;
}): void => {
    Object.keys(globalVariables).forEach((key) => {
        if (!options?.exclude?.includes(key)) {
            delete globalVariables[key];
        }
    });
};

/**
 * Clear a single prompt variable.
 */
export const clearPromptVariable = (key: string): void => {
    delete globalVariables[key];
};

/**
 * Payload for the `set-prompt-variable` command.
 */
export interface SetPromptVariablePayload {
    name: string;
    value: string | null;
}

/**
 * Parse an incoming `set-prompt-variable` command payload.
 */
export function parseSetPromptVariablePayload(
    content: string,
): SetPromptVariablePayload | null {
    const payload = tryParseJSON(content) as {
        name: string;
        value: string;
    } | null;
    if (!payload) {
        return null;
    }
    const name = payload?.name;
    if (!name) {
        return null;
    }
    const value = payload?.value;
    return {name, value};
}

/**
 * Parse a JSON string or give up with null.
 */
function tryParseJSON(content: string): unknown | null {
    try {
        return JSON.parse(content);
    } catch (e) {
        return null;
    }
}
